import { FilteredCategoryItemGroup } from '@/contexts/item-context-types';
import {
  SearchFilterResponse,
  VariantPackInfoContext_GroupBadgeFieldsFragment,
} from '@/types';
import { useGTINMetadataFilters } from '@/hooks/use-gtin-metadata-filters';

export interface Option {
  depth?: number;
  subtitle?: string;
  title: string;
  value: string;
}

export interface SearchFilterOption<K> {
  key: K;
  options: Option[];
  selected: Set<string>;
  title: string;
}

export interface TypesenseSearchFilters {
  filters: TypesenseSearchFilter[];
  resetFilters: () => void;
  selected: SearchFilterResponse[];
}

export interface TypesenseSearchFilter {
  displayStrategy?: string;
  filter: Set<string>;
  isAvailable: boolean;
  key: string;
  label: string;
  options: Option[];
  updateFilter: (selectedValues: Set<string>) => void;
}

export enum SearchFilterDisplayStrategy {
  DROPDOWN = 'DROPDOWN',
  TOGGLE = 'TOGGLE',
}

export interface SearchFilters {
  badges: VariantPackInfoContext_GroupBadgeFieldsFragment[];
  badgesFilter: Set<string>;
  gtinFilters: ReturnType<typeof useGTINMetadataFilters>['gtinFilters'];
  searchFilters: TypesenseSearchFilters;
  setBadgesFilter: React.Dispatch<React.SetStateAction<Set<string>>>;
  setSubcategoriesFilter: React.Dispatch<React.SetStateAction<Set<string>>>;
  subcategories: FilteredCategoryItemGroup[];
  subcategoriesFilter: Set<string>;
}

// Helper function to recursively transform FilteredCategoryItemGroup to Option
export const transformItemGroupToFilterOption = (
  data: FilteredCategoryItemGroup[],
): Option[] => {
  const transformToFlatOptions = (
    category: FilteredCategoryItemGroup,
    depth: number,
  ): Option[] => {
    if (!category.uuid) {
      return [];
    }

    const currentOption: Option = {
      depth,
      title: category.name,
      value: category.path,
    };

    const subOptions =
      category.directSubcategories?.flatMap((subcategory) =>
        transformToFlatOptions(subcategory, depth + 1),
      ) || [];

    return [currentOption, ...subOptions];
  };

  return data.flatMap((category) => transformToFlatOptions(category, 0));
};

export const isInCategoryItemGroups = (
  categoryItemGroups: FilteredCategoryItemGroup[],
  filterSet: Set<string>,
): boolean => {
  return Array.from(filterSet).every((path) =>
    categoryItemGroups.some((group) => {
      return (
        group.path === path ||
        isInCategoryItemGroups(group.directSubcategories ?? [], new Set([path]))
      );
    }),
  );
};

export const getParents = (path: string) => {
  const parents = path.split('/').slice(0, -1);
  return parents.map((_, index) => parents.slice(0, index + 1).join('/'));
};

export const getChildren = (path: string, options: Option[]) => {
  return options.filter(
    (option) => option.value.startsWith(`/${path}`) && option.value !== path,
  );
};
